// Analytics & Logs - Charts, metrics, and CSV export
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import {
    collection,
    query,
    where,
    getDocs,
    orderBy,
    limit,
    Timestamp
} from 'firebase/firestore';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    LinearProgress
} from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

const Analytics = () => {
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('7');
    const [auditLogs, setAuditLogs] = useState([]);
    const [reportsTrend, setReportsTrend] = useState([]);
    const [userStats, setUserStats] = useState({ total: 0, newThisWeek: 0, active: 0 });
    const [reportsStats, setReportsStats] = useState({ total: 0, resolved: 0, pending: 0 });

    useEffect(() => {
        loadAnalytics();
    }, [dateRange]);

    const loadAnalytics = async () => {
        setLoading(true);
        try {
        const daysAgo = parseInt(dateRange, 10);
        const startDate = Timestamp.fromDate(new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000));
        const safe = async (fn, fallback) => {
            try {
                return await fn();
            } catch (error) {
                console.error(error);
                return fallback;
            }
        };

        const logs = await safe(async () => {
            const auditSnapshot = await getDocs(query(
                collection(db, 'admin_audit_log'),
                orderBy('performedAt', 'desc'),
                limit(50)
            ));
            return auditSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        }, []);
        setAuditLogs(logs);

        const usersSnapshot = await safe(
            () => getDocs(collection(db, 'users')),
            { size: 0, forEach: () => {}, docs: [] }
        );
        const newUsersSnapshot = await safe(
            () => getDocs(query(collection(db, 'users'), where('createdAt', '>', startDate))),
            { size: 0, forEach: () => {}, docs: [] }
        );
        const activeDate = Timestamp.fromDate(new Date(Date.now() - 5 * 60 * 1000));
        const activeSnapshot = await safe(
            () => getDocs(query(collection(db, 'users'), where('lastSeen', '>', activeDate))),
            { size: 0, forEach: () => {}, docs: [] }
        );
        setUserStats({
            total: usersSnapshot.size || 0,
            newThisWeek: newUsersSnapshot.size || 0,
            active: activeSnapshot.size || 0,
        });

        const allReportsSnapshot = await safe(
            () => getDocs(collection(db, 'moderation_reports')),
            { size: 0, forEach: () => {}, docs: [] }
        );
        const pendingSnapshot = await safe(
            () => getDocs(query(collection(db, 'moderation_reports'), where('status', '==', 'pending'))),
            { size: 0 }
        );
        const resolvedSnapshot = await safe(
            () => getDocs(query(collection(db, 'moderation_reports'), where('status', '==', 'resolved'))),
            { size: 0 }
        );
        setReportsStats({
            total: allReportsSnapshot.size || 0,
            pending: pendingSnapshot.size || 0,
            resolved: resolvedSnapshot.size || 0,
        });

        const trendData = [];
        const buckets = {};
        for (let i = daysAgo - 1; i >= 0; i--) {
            const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
            const key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            buckets[key] = { date: key, reports: 0, resolved: 0, newUsers: 0 };
        }
        if (typeof allReportsSnapshot.forEach === 'function') {
            allReportsSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const created = data.createdAt?.toDate?.();
                if (!created) return;
                const key = created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                if (!buckets[key]) return;
                buckets[key].reports += 1;
                if (data.status === 'resolved') buckets[key].resolved += 1;
            });
        }
        if (typeof newUsersSnapshot.forEach === 'function') {
            newUsersSnapshot.forEach((docSnap) => {
                const created = docSnap.data().createdAt?.toDate?.();
                if (!created) return;
                const key = created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                if (buckets[key]) buckets[key].newUsers += 1;
            });
        }
        Object.values(buckets).forEach((row) => trendData.push(row));
        setReportsTrend(trendData);
        } finally {
            setLoading(false);
        }
    };

    const exportToCSV = (data, filename) => {
        if (data.length === 0) return;

        const headers = Object.keys(data[0]);
        const csvContent = [
            headers.join(','),
            ...data.map(row =>
                headers.map(header => {
                    const value = row[header];
                    if (value?.toDate) return value.toDate().toISOString();
                    if (typeof value === 'object') return JSON.stringify(value);
                    return value;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Unknown';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString();
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Analytics & Logs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Community metrics, trends, and audit trail
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Period</InputLabel>
                        <Select
                            value={dateRange}
                            label="Period"
                            onChange={(e) => setDateRange(e.target.value)}
                        >
                            <MenuItem value="7">Last 7 days</MenuItem>
                            <MenuItem value="14">Last 14 days</MenuItem>
                            <MenuItem value="30">Last 30 days</MenuItem>
                            <MenuItem value="90">Last 90 days</MenuItem>
                        </Select>
                    </FormControl>
                    <Button startIcon={<RefreshIcon />} onClick={loadAnalytics}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Total Users</Typography>
                            <Typography variant="h3" fontWeight="bold" color="primary">
                                {userStats.total.toLocaleString()}
                            </Typography>
                            <Chip
                                size="small"
                                label={`+${userStats.newThisWeek} this week`}
                                color="success"
                                sx={{ mt: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Reports (Total/Pending)</Typography>
                            <Typography variant="h3" fontWeight="bold" sx={{ color: '#FF6B9D' }}>
                                {reportsStats.total} / {reportsStats.pending}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {reportsStats.resolved} resolved
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Active Now</Typography>
                            <Typography variant="h3" fontWeight="bold" sx={{ color: '#4ECDC4' }}>
                                {userStats.active}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Users online in last 5 min
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, xl: 8 }} sx={{ minWidth: 0 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Activity Trend
                            </Typography>
                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                <Box sx={{ width: '100%', minWidth: 480, height: 320 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={reportsTrend} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis width={40} />
                                        <Tooltip />
                                        <Legend />
                                        <Line type="monotone" dataKey="newUsers" stroke="#4ECDC4" name="New Users" />
                                        <Line type="monotone" dataKey="reports" stroke="#FF6B9D" name="Reports" />
                                        <Line type="monotone" dataKey="resolved" stroke="#96CEB4" name="Resolved" />
                                    </LineChart>
                                </ResponsiveContainer>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, xl: 4 }} sx={{ minWidth: 0 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Report Resolution
                            </Typography>
                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                <Box sx={{ width: '100%', minWidth: 280, height: 320 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={[
                                        { name: 'Pending', value: reportsStats.pending, fill: '#FF6B9D' },
                                        { name: 'Resolved', value: reportsStats.resolved, fill: '#4ECDC4' }
                                    ]} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis width={40} />
                                        <Tooltip />
                                        <Bar dataKey="value" />
                                    </BarChart>
                                </ResponsiveContainer>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Audit Log */}
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                            Admin Audit Log
                        </Typography>
                        <Button
                            startIcon={<DownloadIcon />}
                            onClick={() => exportToCSV(auditLogs, 'audit_log')}
                            disabled={auditLogs.length === 0}
                        >
                            Export CSV
                        </Button>
                    </Box>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Action</TableCell>
                                <TableCell>Target</TableCell>
                                <TableCell>Performed By</TableCell>
                                <TableCell>Note</TableCell>
                                <TableCell>Time</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {auditLogs.slice(0, 20).map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={log.action}
                                            color={
                                                log.action === 'ban' ? 'error' :
                                                    log.action === 'warn' ? 'warning' :
                                                        'default'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {log.targetUserId?.substring(0, 8)}...
                                    </TableCell>
                                    <TableCell>
                                        {log.performedBy?.substring(0, 8)}...
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {log.note || '-'}
                                    </TableCell>
                                    <TableCell>{formatDate(log.performedAt)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {auditLogs.length === 0 && (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No audit logs yet
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default Analytics;
